import { useAppGate } from '@/features/setup/gate/use-app-gate';
import {
  areServicesEnabled,
  Coords,
  FRESH_FIX_ATTEMPTS,
  FRESH_FIX_RETRY_DELAY_MS,
  getFreshCoords,
  getLastKnownCoords,
  getPermissionState,
  LOCATION_TIMEOUT_MS,
  LocationPermissionState,
  requestPermission,
} from '@/utils/location-source';
import { DEFAULT_LOCATION, DefaultLocation } from '@/utils/location-defaults';
import { roundCoords } from '@/utils/round-coords';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';

/**
 * Why a location fix is or isn't available.
 *
 * `denied`, `services-disabled` and `unavailable` are deliberately distinct: they
 * have different causes, different fixes, and different copy. Collapsing them is
 * what made a location-less emulator report "Location access needed" to users who
 * had granted permission.
 */
export type LocationStatus =
  | 'idle'
  | 'requesting'
  | 'granted'
  | 'denied'
  | 'services-disabled'
  | 'unavailable';

export interface LocationState {
  status: LocationStatus;
  coords: Coords | null;
  defaultLocation: DefaultLocation;
  refresh: () => Promise<void>;
  isRefreshing: boolean;
}

/** Statuses worth retrying when the app returns to the foreground. */
const RECOVERABLE: readonly LocationStatus[] = ['denied', 'services-disabled', 'unavailable'];

async function readPermission(): Promise<LocationPermissionState> {
  try {
    return await getPermissionState();
  } catch {
    // The platform could not tell us. That is not a refusal.
    return 'undetermined';
  }
}

async function askPermission(): Promise<LocationPermissionState> {
  try {
    return await requestPermission();
  } catch {
    return 'undetermined';
  }
}

/**
 * Engine behind {@link LocationProvider}: resolves the user's location, or an
 * honest reason why it couldn't.
 *
 * Acquisition is bounded at every step -- it never starts a position call that
 * cannot succeed (permission missing, services off) and caps the one it does
 * start at {@link LOCATION_TIMEOUT_MS} -- and it never starts at all until the
 * app gate says the app itself is open.
 *
 * Consume this via `useUserLocation()` rather than calling it directly, so the
 * whole app shares one acquisition.
 */
export function useLocationState(): LocationState {
  const [status, setStatus] = useState<LocationStatus>('idle');
  const [coords, setCoords] = useState<Coords | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // The app gate, not auth, is the trigger: verified-but-still-in-setup is
  // precisely when the OS dialog used to fire over the username screen.
  const { gate } = useAppGate();
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const statusRef = useRef<LocationStatus>('idle');

  statusRef.current = status;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const acquire = useCallback(async () => {
    // A second pass while one is running would only race the first to the same answer.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsRefreshing(true);

    try {
      setStatus('requesting');

      let permission = await readPermission();
      if (permission === 'undetermined') {
        permission = await askPermission();
      }
      if (!mountedRef.current) return;

      if (permission === 'denied') {
        setStatus('denied');
        setCoords(null);
        return;
      }
      if (permission !== 'granted') {
        // Still unknown after asking -- we cannot claim the user refused.
        setStatus('unavailable');
        setCoords(null);
        return;
      }

      // Fail fast: with services off, a position call can only hang or fail.
      const servicesEnabled = await areServicesEnabled();
      if (!mountedRef.current) return;
      if (!servicesEnabled) {
        setStatus('services-disabled');
        setCoords(null);
        return;
      }

      // Paint whatever the OS already has while the fresh fix is still in flight.
      // Round before storing: coords become the nearby query key, so a stable
      // ~110m bucket lets the persisted cache survive GPS jitter between sessions.
      const lastKnown = await getLastKnownCoords();
      if (!mountedRef.current) return;
      if (lastKnown) {
        setStatus('granted');
        setCoords(roundCoords(lastKnown));
      }

      // A single fresh call is enough on native (its last-known cache is instant),
      // but web has no cache and a cold geolocation subsystem on first page load,
      // so the first call routinely times out. Retry patiently -- stay in the
      // spinner between attempts and only declare `unavailable` once exhausted,
      // rather than terminally giving up on a fix that a warm retry would land.
      let fresh: Coords | null = null;
      for (let attempt = 0; attempt < FRESH_FIX_ATTEMPTS; attempt++) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, FRESH_FIX_RETRY_DELAY_MS));
          if (!mountedRef.current) return;
        }
        try {
          fresh = await getFreshCoords(LOCATION_TIMEOUT_MS);
        } catch {
          fresh = null;
        }
        if (!mountedRef.current) return;
        if (fresh) break;
      }

      if (fresh) {
        setStatus('granted');
        setCoords(roundCoords(fresh));
      } else if (!lastKnown) {
        setStatus('unavailable');
        setCoords(null);
      }
      // A stale fix beats no fix: never downgrade a usable answer.
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setIsRefreshing(false);
    }
  }, []);

  // Gated on the app being open (ADR 0006), so the OS location prompt never
  // appears on the login screen *or* over a setup step. Testing for `'app'`
  // rather than negating `needsSetup` is deliberate: `booting` and `auth` are
  // also not-setup, and a bare negation would let acquisition start during cold
  // boot -- reintroducing the interruption this gate exists to prevent.
  useEffect(() => {
    if (gate !== 'app') {
      setStatus('idle');
      setCoords(null);
      return;
    }
    void acquire();
  }, [gate, acquire]);

  // Returning from Settings should heal the screen without a tap, but a working
  // fix must not re-hit the GPS on every foreground.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState !== 'active') return;
      if (!RECOVERABLE.includes(statusRef.current)) return;
      void acquire();
    });
    return () => subscription?.remove();
  }, [acquire]);

  return {
    status,
    coords,
    defaultLocation: DEFAULT_LOCATION,
    refresh: acquire,
    isRefreshing,
  };
}
